import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema, type Timestamp } from '@bufbuild/protobuf/wkt'

import {
  CreateQuoteRequestSchema,
  UpdateQuoteRequestSchema,
  type CreateQuoteRequest,
  type UpdateQuoteRequest,
} from '@/gen/cortado/v1/cortado_pb'
import {
  QuoteAddonOfferInputSchema,
  QuoteLineItemInputSchema,
  type Item,
  type Quote,
  type QuoteAddonOffer,
  type QuoteAddonOfferInput,
  type QuoteEvent,
  type QuoteLineItem as QuoteLineItemMessage,
  type QuoteLineItemInput,
} from '@/gen/cortado/v1/invoices_pb'
import {
  DiscountTypeEnum,
  ItemUnitEnum,
  QuotePaymentScheduleDueDateAnchorEnum,
  QuoteStatusEnum,
  StripeCheckoutPaymentMethodEnum,
  type TipAllocationModeEnum,
} from '@/gen/cortado/v1/settings_pb'
import { normalizeTipAllocationMode } from '@/components/shared/financial-document/tip-allocation-mode'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import type { QuoteEventFormValue } from '@/components/quotes/quote-event-mapping'
import type { QuoteAddonOfferFormValue } from '@/components/quotes/quote-addon-offers-table'
import { haveQuoteAddonOffersChanged } from '@/components/quotes/quote-addon-offer-changes'
import {
  inquiryEventsToQuoteEventFormValues,
  quoteEventFormValuesToInputs,
  type CopyableInquiryEvent,
} from '@/components/quotes/quote-event-mapping'
import { resolveInquiryEventLocation } from '@/components/quotes/quote-event-location'
import { getDefaultAddonQuantity } from '@/lib/addon-defaults'
import { shouldSeedAddonOffer } from '@/lib/addon-offer-filters'
import { dollarsToCents } from '@/lib/currency-utils'
import { itemUnitEnumToString, stringToItemUnitEnum } from '@/lib/enum-utils'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import {
  formValuesToPaymentSchedules,
  paymentSchedulesToFormValues,
  type PaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { normalizeDueDateAnchor } from '@/lib/quote-payment-schedule-defaults'
import { getQuotePaymentScheduleValidationIssue } from '@/lib/quote-payment-schedule-validation'
import { timestampToDate } from '@/lib/inquiry-utils'
import { dateToTimestamp } from '@/lib/date-utils'

export type QuoteDiscountType = DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED

/** The editable surface of a quote in the pipeline quote editor. */
export interface QuoteEditorFormValues {
  title: string
  /** TipTap HTML; '' when empty. */
  description: string
  validUntil?: Date
  events: QuoteEventFormValue[]
  /** One flat list, transportation ("Per Mile") lines excluded. */
  lineItems: QuoteLineItem[]
  /** The mileage-priced transportation line(s), kept apart from the other lines. */
  transportationLines: QuoteLineItem[]
  /** A saved discount's id; unset for a custom (one-time) discount or none. */
  discountId?: string
  discountName?: string
  discountType?: QuoteDiscountType
  /** Percent (0-100) for PERCENTAGE, dollars for FIXED. */
  discountValue?: number
  enforcedTipEnabled: boolean
  /** Percent, 0-100. */
  enforcedTipPercentage?: number
  /** How the tip is spread across payment schedules. Never UNSPECIFIED. */
  tipAllocationMode: TipAllocationModeEnum
  /** Optional add-ons the customer can opt into; not part of the total. */
  addonOffers: QuoteAddonOfferFormValue[]
  /** Empty means the backend's default single payment. */
  paymentSchedules: PaymentScheduleFormValue[]
  /** Off means the org's default checkout methods (sent as an empty list). */
  useCustomPaymentMethods: boolean
  /** At least one when custom. */
  checkoutPaymentMethods: StripeCheckoutPaymentMethodEnum[]
  /** Accepting the quote attaches a questionnaire to the booking. */
  requiresQuestionnaire: boolean
}

/** Shown, and saved when custom methods are first switched on, while the org defaults apply. */
export const DEFAULT_CHECKOUT_PAYMENT_METHODS = [
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD,
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT,
]

function checkoutMethodsToSend(values: QuoteEditorFormValues): StripeCheckoutPaymentMethodEnum[] {
  return values.useCustomPaymentMethods ? [...values.checkoutPaymentMethods] : []
}

/** How a quote in a given status can be edited and saved. */
export interface QuoteEditorAccess {
  /** `autosave` for drafts, `manual` (Save / Discard) once sent, `none` when read-only. */
  saveMode: 'autosave' | 'manual' | 'none'
  /** Title, description and valid-until. */
  canEditHeader: boolean
  /** Events, line items, add-ons, discount, tip and payment. Accepted quotes keep their financials locked by the backend. */
  canEditContent: boolean
}

const READ_ONLY: QuoteEditorAccess = { saveMode: 'none', canEditHeader: false, canEditContent: false }

/** Edit policy per status. A quote not yet created (`undefined`) is a draft. */
export function quoteEditorAccess(status: QuoteStatusEnum | undefined): QuoteEditorAccess {
  switch (status) {
    case undefined:
    case QuoteStatusEnum.QUOTE_STATUS_DRAFT:
      return { saveMode: 'autosave', canEditHeader: true, canEditContent: true }
    case QuoteStatusEnum.QUOTE_STATUS_SENT:
    case QuoteStatusEnum.QUOTE_STATUS_VIEWED:
    case QuoteStatusEnum.QUOTE_STATUS_REVISED:
      return { saveMode: 'manual', canEditHeader: true, canEditContent: true }
    case QuoteStatusEnum.QUOTE_STATUS_ACCEPTED:
      return { saveMode: 'manual', canEditHeader: true, canEditContent: false }
    default:
      return READ_ONLY
  }
}

const PER_MILE_UNIT = ItemUnitEnum.ITEM_UNIT_PER_MILE

function lineItemUnit(unit: string | undefined): ItemUnitEnum {
  return unit ? stringToItemUnitEnum(unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT
}

/** True for the quote's transportation ("Per Mile") line. */
export function isTransportationLine(line: QuoteLineItemMessage): boolean {
  return lineItemUnit(line.unit) === PER_MILE_UNIT
}

/** TipTap renders an empty document as `<p></p>`; treat that as no description. */
export function normalizeDescriptionHtml(html: string | undefined): string {
  const trimmed = (html ?? '').trim()
  return trimmed === '<p></p>' ? '' : trimmed
}

function timestampMillis(timestamp: Timestamp | undefined): number | undefined {
  return timestamp ? timestampToDate(timestamp)?.getTime() : undefined
}

function quoteLineToFormItem(line: QuoteLineItemMessage, sortOrder: number): QuoteLineItem {
  return {
    id: line.id || undefined,
    tempId: crypto.randomUUID(),
    sourceItemId: line.sourceItemId || undefined,
    name: line.name,
    description: line.description ?? '',
    quantity: line.quantity,
    unitPriceCents: Number(line.unitPriceCents),
    unit: lineItemUnit(line.unit),
    sortOrder,
    lineTotalCents: Number(line.lineTotalCents),
    isTaxable: line.isTaxable,
    salesTaxRateId: line.salesTaxRateId || undefined,
    salesTaxRateName: line.salesTaxRateName || undefined,
    salesTaxRate: line.salesTaxRate ?? undefined,
    salesTaxCents: Number(line.salesTaxCents),
  }
}

function quoteEventToFormValue(event: QuoteEvent): QuoteEventFormValue {
  return {
    tempId: crypto.randomUUID(),
    id: event.id || undefined,
    eventDate: event.eventDate ? timestampToDate(event.eventDate) : undefined,
    eventLength: event.eventLength,
    eventGuests: event.eventGuests,
    eventTimezone: event.eventTimezone,
    eventLocation: event.eventLocation,
    baristaCount: event.baristaCount,
    cartCount: event.cartCount,
    sourceInquiryEventId: event.sourceInquiryEventId || undefined,
  }
}

function sanitizeDiscountType(type: DiscountTypeEnum | undefined): QuoteDiscountType | undefined {
  return type === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE || type === DiscountTypeEnum.DISCOUNT_TYPE_FIXED
    ? type
    : undefined
}

function addonOfferToFormValue(offer: QuoteAddonOffer): QuoteAddonOfferFormValue {
  return {
    id: offer.id || undefined,
    tempId: crypto.randomUUID(),
    sourceItemId: offer.sourceItemId,
    name: offer.name,
    description: offer.description ?? '',
    unitPriceCents: Number(offer.unitPriceCents),
    unit: lineItemUnit(offer.unit),
    quantity: offer.quantity,
    // Kept as stored: the change check compares sort order as-is.
    sortOrder: offer.sortOrder,
    isTaxable: offer.isTaxable ?? false,
    salesTaxRateId: offer.salesTaxRateId,
    salesTaxRateName: offer.salesTaxRateName,
    salesTaxRate: offer.salesTaxRate,
  }
}

function addonOfferToInput(offer: QuoteAddonOfferFormValue, index: number): QuoteAddonOfferInput {
  return create(QuoteAddonOfferInputSchema, {
    id: offer.id,
    itemId: offer.sourceItemId,
    name: offer.name,
    description: offer.description,
    unitPriceCents: BigInt(offer.unitPriceCents),
    unit: itemUnitEnumToString(offer.unit),
    quantity: offer.quantity,
    sortOrder: index,
    isTaxable: offer.isTaxable,
    salesTaxRateId: offer.salesTaxRateId,
    salesTaxRateName: offer.salesTaxRateName,
    salesTaxRate: offer.salesTaxRate,
  })
}

/** A saved quote as editor form values. */
export function quoteToFormValues(quote: Quote): QuoteEditorFormValues {
  return {
    addonOffers: quote.addonOffers.map(addonOfferToFormValue),
    paymentSchedules: paymentSchedulesToFormValues(quote.paymentSchedules),
    useCustomPaymentMethods: quote.checkoutPaymentMethods.length > 0,
    checkoutPaymentMethods: quote.checkoutPaymentMethods.length > 0
      ? [...quote.checkoutPaymentMethods]
      : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
    requiresQuestionnaire: quote.requiresQuestionnaire,
    discountId: quote.discountId || undefined,
    discountName: quote.discountName || undefined,
    discountType: sanitizeDiscountType(quote.discountType),
    discountValue: quote.discountValue || undefined,
    enforcedTipEnabled: quote.enforcedTipEnabled ?? false,
    enforcedTipPercentage: quote.enforcedTipPercentage || undefined,
    tipAllocationMode: normalizeTipAllocationMode(quote.tipAllocationMode),
    title: quote.title,
    description: normalizeDescriptionHtml(quote.description),
    validUntil: quote.validUntil ? timestampToDate(quote.validUntil) : undefined,
    events: quote.quoteEvents.map(quoteEventToFormValue),
    lineItems: quote.lineItems
      .filter((line) => !isTransportationLine(line))
      .map((line, index) => quoteLineToFormItem(line, index)),
    transportationLines: quoteTransportationItems(quote),
  }
}

/** The quote's transportation line(s), counted in its totals. */
export function quoteTransportationItems(quote: Quote | undefined): QuoteLineItem[] {
  return (quote?.lineItems ?? []).filter(isTransportationLine).map((line, index) => quoteLineToFormItem(line, index))
}

function formItemToInput(item: QuoteLineItem): QuoteLineItemInput {
  // No quote_revision_event_id: the editor keeps one flat list, not per-event groups.
  return create(QuoteLineItemInputSchema, {
    itemId: item.sourceItemId || undefined,
    quantity: item.quantity,
    unitPriceCents: BigInt(item.unitPriceCents),
    // Name, description and unit are always sent so catalog overrides survive.
    name: item.name,
    description: item.description,
    unit: itemUnitEnumToString(item.unit),
    isTaxable: item.isTaxable,
    salesTaxRateId: item.salesTaxRateId,
    salesTaxRateName: item.salesTaxRateName,
    salesTaxRate: item.salesTaxRate,
    // No sales_tax_cents: the server computes each line's tax and ignores a client value.
  })
}

/** A saved line item re-sent exactly as stored (used for an unchanged transportation line). */
function savedLineToInput(line: QuoteLineItemMessage): QuoteLineItemInput {
  return create(QuoteLineItemInputSchema, {
    itemId: line.sourceItemId || undefined,
    quantity: line.quantity,
    unitPriceCents: line.unitPriceCents,
    name: line.name,
    description: line.description,
    unit: line.unit,
    isTaxable: line.isTaxable,
    salesTaxRateId: line.salesTaxRateId || undefined,
    salesTaxRateName: line.salesTaxRateName || undefined,
    salesTaxRate: line.salesTaxRate,
  })
}

function lineItemsDiffer(saved: QuoteLineItemMessage[], items: QuoteLineItem[]): boolean {
  if (saved.length !== items.length) return true
  return saved.some((line, index) => {
    const item = items[index]
    return (
      (line.sourceItemId || undefined) !== (item.sourceItemId || undefined) ||
      line.name !== item.name ||
      (line.description ?? '') !== (item.description ?? '') ||
      line.quantity !== item.quantity ||
      Number(line.unitPriceCents) !== item.unitPriceCents ||
      lineItemUnit(line.unit) !== item.unit ||
      line.isTaxable !== item.isTaxable ||
      (line.salesTaxRateId || undefined) !== (item.salesTaxRateId || undefined) ||
      (line.salesTaxRate ?? undefined) !== (item.salesTaxRate ?? undefined)
    )
  })
}

function eventsDiffer(saved: QuoteEvent[], events: QuoteEventFormValue[]): boolean {
  if (saved.length !== events.length) return true
  const num = (value: number | undefined) => value ?? undefined
  const str = (value: string | undefined) => value || ''
  return saved.some((event, index) => {
    const next = events[index]
    return (
      timestampMillis(event.eventDate) !== next.eventDate?.getTime() ||
      num(event.eventLength) !== num(next.eventLength) ||
      num(event.eventGuests) !== num(next.eventGuests) ||
      str(event.eventLocation) !== str(next.eventLocation) ||
      str(event.eventTimezone) !== str(next.eventTimezone) ||
      num(event.baristaCount) !== num(next.baristaCount) ||
      num(event.cartCount) !== num(next.cartCount) ||
      str(event.sourceInquiryEventId) !== str(next.sourceInquiryEventId)
    )
  })
}

/** Start of the local day, as a comparable number. */
function dayKey(date: Date | undefined): number | undefined {
  return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() : undefined
}

/**
 * True when the schedules differ in anything the backend stores. A relative
 * due date (after acceptance / before the event) is derived from its anchor
 * and offset, so only an absolute one is compared by date; a re-derived
 * preview date is never a change.
 */
function paymentSchedulesDiffer(saved: PaymentScheduleFormValue[], next: PaymentScheduleFormValue[]): boolean {
  if (saved.length !== next.length) return true
  return saved.some((schedule, index) => {
    const other = next[index]
    const anchor = normalizeDueDateAnchor(schedule.dueDateAnchor)
    const absolute = anchor === QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE
    return (
      schedule.sequenceNumber !== other.sequenceNumber ||
      schedule.description !== other.description ||
      anchor !== normalizeDueDateAnchor(other.dueDateAnchor) ||
      (absolute ? dayKey(schedule.dueDate) !== dayKey(other.dueDate) : (schedule.dueOffsetDays ?? 0) !== (other.dueOffsetDays ?? 0)) ||
      schedule.amountType !== other.amountType ||
      (schedule.percentage ?? 0) !== (other.percentage ?? 0) ||
      (schedule.amountCents ?? 0) !== (other.amountCents ?? 0)
    )
  })
}

function sameMethods(a: StripeCheckoutPaymentMethodEnum[], b: StripeCheckoutPaymentMethodEnum[]): boolean {
  return a.length === b.length && a.every((method) => b.includes(method))
}

interface DiscountFields {
  discountId?: string
  discountName?: string
  discountType?: DiscountTypeEnum
  discountValue?: number
}

/**
 * A discount's identity: a saved discount by id, a custom one by its
 * name/type/value, or none. A custom discount with no name or a zero value is
 * none, matching the backend, which treats either as clearing.
 */
function discountKey(fields: DiscountFields): string {
  if (fields.discountId) return `saved:${fields.discountId}`
  const type = sanitizeDiscountType(fields.discountType)
  if (fields.discountName?.trim() && type && fields.discountValue) {
    return `custom:${fields.discountName.trim()}:${type}:${fields.discountValue}`
  }
  return 'none'
}

/** The tip in effect: its percentage when enabled with a positive rate, else off. */
function effectiveTipPercentage(enabled: boolean | undefined, percentage: number | undefined): number | undefined {
  return enabled && percentage && percentage > 0 ? percentage : undefined
}

/** Discount request fields: a saved id, a custom name/type/value, or all unset to clear. */
function discountRequestFields(values: QuoteEditorFormValues) {
  const key = discountKey(values)
  if (key === 'none') return {}
  if (values.discountId) return { discountId: values.discountId }
  return {
    discountName: values.discountName?.trim(),
    discountType: values.discountType,
    discountValue: values.discountValue,
  }
}

/**
 * The UpdateQuote request for the edits in `values` against the saved
 * `quote`, or `null` when nothing changed. The mask lists only changed fields:
 * any masked collection (`line_items`, `quote_events`) replaces the whole
 * collection and creates a new backend revision, so unchanged collections are
 * never sent. Content the quote's status locks is never sent either.
 */
export function buildUpdateQuoteRequest(quote: Quote, values: QuoteEditorFormValues): UpdateQuoteRequest | null {
  const access = quoteEditorAccess(quote.status)
  const paths: string[] = []
  const request = create(UpdateQuoteRequestSchema, { id: quote.id })

  if (access.canEditHeader) {
    const title = values.title.trim()
    if (title !== quote.title) {
      paths.push('title')
      request.title = title
    }
    const description = normalizeDescriptionHtml(values.description)
    if (description !== normalizeDescriptionHtml(quote.description)) {
      paths.push('description')
      request.description = description
    }
    // An unset picker is not "clear the date": the backend has no clear for valid_until.
    if (values.validUntil && values.validUntil.getTime() !== timestampMillis(quote.validUntil)) {
      paths.push('valid_until')
      request.validUntil = dateToTimestamp(values.validUntil)
    }
  }

  if (access.canEditContent) {
    const savedTransportation = quote.lineItems.filter(isTransportationLine)
    const transportationChanged = lineItemsDiffer(savedTransportation, values.transportationLines)
    if (transportationChanged || lineItemsDiffer(quote.lineItems.filter((line) => !isTransportationLine(line)), values.lineItems)) {
      paths.push('line_items')
      request.lineItems = [
        ...values.lineItems.map(formItemToInput),
        // line_items replaces the whole list, so an unchanged transportation line goes back exactly as stored.
        ...(transportationChanged
          ? values.transportationLines.map(formItemToInput)
          : savedTransportation.map(savedLineToInput)),
      ]
    }
    if (eventsDiffer(quote.quoteEvents, values.events)) {
      paths.push('quote_events')
      request.quoteEvents = quoteEventFormValuesToInputs(values.events)
    }
    // A masked discount field left unset clears it, so all four go together.
    if (discountKey(values) !== discountKey(quote)) {
      paths.push('discount_id', 'discount_name', 'discount_type', 'discount_value')
      Object.assign(request, discountRequestFields(values))
    }
    if (paymentSchedulesDiffer(paymentSchedulesToFormValues(quote.paymentSchedules), values.paymentSchedules)) {
      paths.push('payment_schedules')
      request.paymentSchedules = formValuesToPaymentSchedules(values.paymentSchedules)
    }
    const methods = checkoutMethodsToSend(values)
    if (!sameMethods(quote.checkoutPaymentMethods, methods)) {
      paths.push('checkout_payment_methods')
      request.checkoutPaymentMethods = methods
    }
    if (values.requiresQuestionnaire !== quote.requiresQuestionnaire) {
      paths.push('requires_questionnaire')
      request.requiresQuestionnaire = values.requiresQuestionnaire
    }
    if (haveQuoteAddonOffersChanged(quote.addonOffers, values.addonOffers)) {
      paths.push('addon_offers')
      request.addonOffers = values.addonOffers.map(addonOfferToInput)
    }
    const tip = effectiveTipPercentage(values.enforcedTipEnabled, values.enforcedTipPercentage)
    if (tip !== effectiveTipPercentage(quote.enforcedTipEnabled, quote.enforcedTipPercentage)) {
      paths.push('enforced_tip_enabled', 'enforced_tip_percentage')
      request.enforcedTipEnabled = tip != null
      request.enforcedTipPercentage = tip
    }
    // Independent of the enforced-tip diff: the mode can change on its own.
    if (
      normalizeTipAllocationMode(values.tipAllocationMode) !== normalizeTipAllocationMode(quote.tipAllocationMode)
    ) {
      paths.push('tip_allocation_mode')
      request.tipAllocationMode = normalizeTipAllocationMode(values.tipAllocationMode)
    }
  }

  if (paths.length === 0) return null
  request.updateMask = create(FieldMaskSchema, { paths })
  return request
}

/** The CreateQuote request for a new quote drafted from `inquiryId`. */
export function buildCreateQuoteRequest(
  inquiryId: string,
  values: QuoteEditorFormValues,
): CreateQuoteRequest {
  const description = normalizeDescriptionHtml(values.description)
  const tip = effectiveTipPercentage(values.enforcedTipEnabled, values.enforcedTipPercentage)
  return create(CreateQuoteRequestSchema, {
    inquiryId,
    title: values.title.trim(),
    description: description || undefined,
    validUntil: values.validUntil ? dateToTimestamp(values.validUntil) : undefined,
    lineItems: [...values.lineItems, ...values.transportationLines].map(formItemToInput),
    quoteEvents: quoteEventFormValuesToInputs(values.events),
    addonOffers: values.addonOffers.map(addonOfferToInput),
    paymentSchedules: formValuesToPaymentSchedules(values.paymentSchedules),
    checkoutPaymentMethods: checkoutMethodsToSend(values),
    requiresQuestionnaire: values.requiresQuestionnaire,
    ...discountRequestFields(values),
    enforcedTipEnabled: tip != null ? true : undefined,
    enforcedTipPercentage: tip,
    tipAllocationMode: normalizeTipAllocationMode(values.tipAllocationMode),
  })
}

export interface QuoteEditorValidationOptions {
  /** Whether payment schedules are editable and so checked against the total (not on locked quotes). */
  checkPaymentSchedules?: boolean
}

/**
 * Why `values` can't be saved yet, mirroring the backend's CreateQuote /
 * UpdateQuote validation, or `undefined` when they can.
 */
export function validateQuoteEditorValues(
  values: QuoteEditorFormValues,
  { checkPaymentSchedules = true }: QuoteEditorValidationOptions = {},
): string | undefined {
  if (!values.title.trim()) return 'Add a title to save this quote.'
  if (values.lineItems.length + values.transportationLines.length === 0) return 'Add at least one line item to save this quote.'
  for (const item of values.lineItems) {
    if (item.unit === PER_MILE_UNIT) return '"Per Mile" is reserved for the transportation line.'
    if (!item.sourceItemId && !item.name.trim()) return 'Every custom line item needs a name.'
    if (!Number.isFinite(item.unitPriceCents)) return `Add a price for ${item.name || 'every custom line item'}.`
  }
  if (!values.discountId && values.discountValue != null) {
    if (values.discountValue < 0) return 'A discount cannot be negative.'
    if (values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE && values.discountValue > 100) {
      return 'A percentage discount must be between 0 and 100.'
    }
  }
  const tip = values.enforcedTipPercentage
  if (values.enforcedTipEnabled && tip != null && (tip < 0 || tip > 100)) {
    return 'The enforced tip must be between 0 and 100%.'
  }
  if (values.useCustomPaymentMethods && values.checkoutPaymentMethods.length === 0) {
    return 'Choose at least one checkout method.'
  }
  if (checkPaymentSchedules) {
    const { totalCents } = calculateQuoteEditorTotals([...values.lineItems, ...values.transportationLines], values)
    const issue = getQuotePaymentScheduleValidationIssue(values.paymentSchedules, totalCents)
    if (issue) return issue.message
  }
  return undefined
}

function preFillLineToFormItem(input: QuoteLineItemInput, sortOrder: number): QuoteLineItem {
  return {
    tempId: crypto.randomUUID(),
    sourceItemId: input.itemId || undefined,
    name: input.name ?? '',
    description: input.description ?? '',
    quantity: input.quantity,
    unitPriceCents: input.unitPriceCents != null ? Number(input.unitPriceCents) : 0,
    unit: lineItemUnit(input.unit),
    sortOrder,
    isTaxable: input.isTaxable,
    salesTaxRateId: input.salesTaxRateId || undefined,
    salesTaxRateName: input.salesTaxRateName || undefined,
    salesTaxRate: input.salesTaxRate ?? undefined,
    salesTaxCents: Number(input.salesTaxCents),
  }
}

function catalogItemToFormItem(item: Item, sortOrder: number): QuoteLineItem {
  return {
    tempId: crypto.randomUUID(),
    sourceItemId: item.id,
    name: item.title,
    description: item.description || '',
    quantity: 1,
    unitPriceCents: dollarsToCents(item.price),
    unit: item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT,
    sortOrder,
    isTaxable: true,
    salesTaxCents: 0,
  }
}

export interface NewQuoteDefaultsInput {
  /** GetQuotePreFill for the inquiry's matching template, when one exists. */
  preFill?: {
    quoteTitle?: string
    quoteDescription?: string
    lineItems: QuoteLineItemInput[]
    quoteEvents: CopyableInquiryEvent[]
  }
  /** Active catalog (transportation items already filtered out). */
  catalogItems: Item[]
  /** Catalog item ids the lead picked on the inquiry. */
  inquiryItemIds: string[]
  inquiryEvents: CopyableInquiryEvent[]
  /** The inquiry's legacy top-level location, used for events without their own. */
  inquiryEventLocation?: string
  /** Used when the template supplies no title, so the draft can save at once. */
  fallbackTitle: string
  /** Computes valid-until from the new quote's event dates (org/event-type expiration rule). */
  defaultValidUntil: (eventDates: Array<Date | undefined>) => Date
  enforcedTipPercentage?: number
  /** The org's default tip collection mode; unset means LAST_SCHEDULE. */
  tipAllocationMode?: TipAllocationModeEnum
  /** The lead's questionnaire setting, which a new quote inherits. */
  requiresQuestionnaire?: boolean
}

/**
 * A new quote's starting values, matching the legacy quote builder: a
 * template's pre-fill when the inquiry's event type has one, else the
 * always-include and lead-picked catalog items with the inquiry's events.
 * Transportation lines never seed (mileage is calculated separately), and
 * flagged add-on catalog items not already on the quote seed as add-on offers.
 */
export function buildNewQuoteDefaults(input: NewQuoteDefaultsInput): QuoteEditorFormValues {
  let lineItems: QuoteLineItem[]
  let events: QuoteEventFormValue[]
  // The lead's events, falling back to the inquiry's legacy top-level location
  // for an event that has none of its own.
  const leadEvents = () =>
    inquiryEventsToQuoteEventFormValues(
      input.inquiryEvents.map((event) => ({
        ...event,
        eventLocation: resolveInquiryEventLocation(event.eventLocation, input.inquiryEventLocation),
      })),
    )
  if (input.preFill) {
    lineItems = input.preFill.lineItems.map((line, index) => preFillLineToFormItem(line, index))
    events = input.preFill.quoteEvents.length > 0 ? inquiryEventsToQuoteEventFormValues(input.preFill.quoteEvents) : leadEvents()
  } else {
    const seen = new Set<string>()
    const picked = new Set(input.inquiryItemIds)
    const seeded = [
      ...input.catalogItems.filter((item) => item.alwaysInclude),
      ...input.catalogItems.filter((item) => picked.has(item.id)),
    ].filter((item) => {
      if (!item.id || seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
    lineItems = seeded.map((item, index) => catalogItemToFormItem(item, index))
    events = leadEvents()
  }
  lineItems = lineItems
    .filter((item) => item.unit !== PER_MILE_UNIT)
    .map((item, index) => ({ ...item, sortOrder: index }))

  const lineSourceIds = new Set(lineItems.map((item) => item.sourceItemId).filter((id): id is string => !!id))
  const addonOffers = input.catalogItems
    .filter((item) => shouldSeedAddonOffer(item, lineSourceIds))
    .sort((a, b) => a.formDisplayOrder - b.formDisplayOrder)
    .map((item, index) => {
      const unit = item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT
      const offer: QuoteAddonOfferFormValue = {
        tempId: crypto.randomUUID(),
        sourceItemId: item.id,
        name: item.title,
        description: item.description || '',
        unitPriceCents: dollarsToCents(item.price),
        unit,
        quantity: getDefaultAddonQuantity(unit, events),
        sortOrder: index,
        isTaxable: true,
      }
      return offer
    })

  return {
    title: input.preFill?.quoteTitle || input.fallbackTitle,
    description: normalizeDescriptionHtml(input.preFill?.quoteDescription),
    validUntil: input.defaultValidUntil(events.map((event) => event.eventDate)),
    events,
    lineItems,
    transportationLines: [],
    addonOffers,
    // No schedule, like the legacy builder: the backend applies its default single payment.
    paymentSchedules: [],
    useCustomPaymentMethods: false,
    checkoutPaymentMethods: [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
    requiresQuestionnaire: input.requiresQuestionnaire ?? false,
    enforcedTipEnabled: (input.enforcedTipPercentage ?? 0) > 0,
    enforcedTipPercentage: (input.enforcedTipPercentage ?? 0) > 0 ? input.enforcedTipPercentage : undefined,
    tipAllocationMode: normalizeTipAllocationMode(input.tipAllocationMode),
  }
}

export interface QuoteEditorTotals {
  subtotalCents: number
  discountCents: number
  salesTaxCents: number
  /** Not part of `totalCents`, matching the backend's persisted total. */
  enforcedTipCents: number
  totalCents: number
  /** `totalCents` plus the enforced tip: what the customer pays at checkout. */
  totalWithTipCents: number
}

/** The totals the server computed for a saved quote — the ones the customer sees. */
export function quoteSavedTotals(quote: Quote): QuoteEditorTotals {
  return {
    subtotalCents: Number(quote.subtotalCents),
    discountCents: Number(quote.discountCents),
    salesTaxCents: Number(quote.salesTaxCents),
    enforcedTipCents: Number(quote.enforcedTipCents ?? 0n),
    totalCents: Number(quote.totalCents),
    totalWithTipCents: Number(quote.totalWithTipCents),
  }
}

/** Mirrors the backend's ApplyDiscount: percent of subtotal or dollars, rounded, clamped to the subtotal. */
function discountCents(subtotalCents: number, values: QuoteEditorFormValues): number {
  if (discountKey(values) === 'none') return 0
  const value = values.discountValue
  if (value == null || value < 0) return 0
  const cents =
    values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE
      ? Math.round(subtotalCents * (value / 100))
      : values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_FIXED
        ? Math.round(value * 100)
        : 0
  return Math.min(cents, subtotalCents)
}

/**
 * Mirrors the backend's prorateLineTotals: spreads `targetCents` across lines
 * in proportion to their totals (largest remainder), so the discounted lines
 * sum exactly to the discounted subtotal.
 */
function prorateLineTotals(lineTotals: number[], targetCents: number): number[] {
  const sourceCents = lineTotals.reduce((sum, cents) => sum + cents, 0)
  if (lineTotals.length === 0 || sourceCents === targetCents) return [...lineTotals]
  if (sourceCents === 0) return lineTotals.map((_, index) => (index === lineTotals.length - 1 ? targetCents : 0))
  const exact = lineTotals.map((cents) => (cents * targetCents) / sourceCents)
  const result = exact.map(Math.floor)
  let leftover = targetCents - result.reduce((sum, cents) => sum + cents, 0)
  if (leftover > 0) {
    // Stable sort by descending remainder, as the backend does.
    const order = exact.map((value, index) => ({ index, frac: value - Math.floor(value) })).sort((a, b) => b.frac - a.frac)
    for (let i = 0; leftover > 0; i++, leftover--) result[order[i % order.length].index]++
  } else if (leftover < 0) {
    result[result.length - 1] += leftover
  }
  return result
}

/**
 * An estimate of the quote's totals, following the backend's rule
 * (CalculateQuoteFinancialsV2): subtotal, a clamped discount, per-line tax on
 * each line's share of the discounted subtotal, and an enforced tip on the
 * discounted subtotal that is excluded from the total. Shown, marked as an
 * estimate, only while edits are unsaved; once saved, the server's totals
 * (`quoteSavedTotals`) replace it. `lineItems` must include every line on the
 * quote, transportation too.
 */
export function calculateQuoteEditorTotals(lineItems: QuoteLineItem[], values: QuoteEditorFormValues): QuoteEditorTotals {
  const lineTotals = lineItems.map((item) => calculateLineTotalCents(item.quantity, item.unitPriceCents))
  const subtotalCents = lineTotals.reduce((sum, cents) => sum + cents, 0)
  const discount = discountCents(subtotalCents, values)
  const afterDiscount = subtotalCents - discount
  const discounted = prorateLineTotals(lineTotals, afterDiscount)
  const salesTaxCents = lineItems.reduce(
    (sum, item, index) => sum + (item.isTaxable ? Math.round(discounted[index] * (item.salesTaxRate ?? 0)) : 0),
    0,
  )
  const tip = effectiveTipPercentage(values.enforcedTipEnabled, values.enforcedTipPercentage)
  const enforcedTipCents = tip != null && tip <= 100 ? Math.round(afterDiscount * (tip / 100)) : 0
  return {
    subtotalCents,
    discountCents: discount,
    salesTaxCents,
    enforcedTipCents,
    totalCents: subtotalCents - discount + salesTaxCents,
    totalWithTipCents: subtotalCents - discount + salesTaxCents + enforcedTipCents,
  }
}
